import { useState, useEffect, useRef, forwardRef } from "react";
import api from "../lib/api";

/**
 * MentionTextarea — drop-in Textarea replacement that detects '@' typing and
 * pops up a list of users. Selecting one inserts `@Username` (no spaces) into
 * the value so the backend mention parser can resolve it.
 */
const MentionTextarea = forwardRef(function MentionTextarea(
  { value, onChange, placeholder, rows = 2, className = "", "data-testid": testId, disabled },
  forwardedRef
) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const taRef = useRef(null);
  const setRef = (el) => {
    taRef.current = el;
    if (typeof forwardedRef === "function") forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };

  useEffect(() => {
    api.get("/users/directory").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const extractQuery = (text, cursor) => {
    const before = text.slice(0, cursor);
    const m = before.match(/@([A-Za-z][A-Za-z0-9._-]*)$/);
    return m ? m[1] : null;
  };

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(e);
    const cursor = e.target.selectionStart || text.length;
    const q = extractQuery(text, cursor);
    if (q !== null) {
      setQuery(q);
      setOpen(true);
      setHighlight(0);
    } else {
      setOpen(false);
    }
  };

  const filtered = query === null
    ? []
    : users
        .filter((u) => {
          const q = query.toLowerCase();
          return (
            u.name?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().startsWith(q)
          );
        })
        .slice(0, 6);

  const insertMention = (u) => {
    const ta = taRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart || value.length;
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const m = before.match(/@([A-Za-z][A-Za-z0-9._-]*)$/);
    if (!m) return;
    const handle = u.name.replace(/\s+/g, "");
    const newBefore = before.slice(0, before.length - m[0].length) + `@${handle} `;
    const newValue = newBefore + after;
    onChange({ target: { value: newValue } });
    setOpen(false);
    setTimeout(() => {
      if (taRef.current) {
        taRef.current.focus();
        const pos = newBefore.length;
        taRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={setRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={`flex w-full rounded-none border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        data-testid={testId}
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 shadow-lg z-50 max-h-52 overflow-y-auto"
          data-testid="mention-dropdown"
        >
          {filtered.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 text-xs ${
                i === highlight ? "bg-blue-50" : "hover:bg-slate-50"
              }`}
              data-testid={`mention-option-${i}`}
            >
              <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center">
                {u.name?.slice(0, 1).toUpperCase()}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-medium text-slate-800 truncate">{u.name}</span>
                <span className="block text-[10px] text-slate-400 truncate">
                  {u.role} · {u.email}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default MentionTextarea;
